<template>
<div>
    <section class="position-relative pt-12 pt-md-14">
        <div class="container" style="max-width: 900px;">
            <!-- Banner -->
            <div class="status-banner" :class="bannerClass">
                <h1 class="status-banner-title">{{ bannerTitle }}</h1>
                <p class="status-banner-sub" v-if="data.updated_at">Last updated: {{ formatUTC(data.updated_at) }}</p>
            </div>

            <template v-if="loaded">
                <!-- 90 days -->
                <div class="section-title">Uptime — last 90 days</div>
                <div v-for="group in groups" :key="'d-' + group.key" class="status-section">
                    <div class="status-header">
                        <div class="status-name">{{ group.label }}</div>
                        <div class="status-label" :class="statusClass(componentStatus(group.key))">
                            {{ componentStatusLabel(group.key) }}
                        </div>
                    </div>
                    <div class="uptime-bars" :class="{ large: group.key === 'all' }">
                        <div v-for="(d, i) in data.daily" :key="i"
                             class="uptime-bar"
                             :class="barColor(d[group.key])"
                             @mouseenter="showTooltip($event, dailyTooltip(d, group.key))"
                             @mouseleave="hideTooltip()">
                        </div>
                    </div>
                    <div class="bar-legend">
                        <span>90 days ago</span>
                        <span>{{ componentUptime(group.key) }}% uptime</span>
                        <span>Today</span>
                    </div>
                </div>

                <template v-if="false">
                <hr class="divider">

                <div class="section-title">Uptime — last 24 hours (every 15 min)</div>
                <div v-for="group in groups" :key="'h-' + group.key" class="status-section">
                    <div class="status-header">
                        <div class="status-name">{{ group.label }}</div>
                    </div>
                    <div class="uptime-bars">
                        <div v-for="(d, i) in data.intervals" :key="i"
                             class="uptime-bar"
                             :class="barColor(d[group.key])"
                             @mouseenter="showTooltip($event, intervalTooltip(d, group.key))"
                             @mouseleave="hideTooltip()">
                        </div>
                    </div>
                    <div class="bar-legend">
                        <span>24 hours ago</span>
                        <span>{{ intervalUptime(group.key) }}% uptime</span>
                        <span>Now</span>
                    </div>
                </div>

                <hr class="divider">

                <div class="section-title">Response Time — last 24 hours (p50, every 15 min)</div>
                <div v-for="group in groups" :key="'r-' + group.key" class="status-section">
                    <div class="status-header">
                        <div class="status-name">{{ group.label }}</div>
                    </div>
                    <div class="current-stats" v-if="lastInterval(group.key)">
                        <div>Current: <span>{{ lastInterval(group.key).p50 }}ms</span></div>
                    </div>
                    <div class="rt-bars">
                        <div v-for="(d, i) in data.intervals" :key="i"
                             class="rt-bar"
                             :style="{ height: rtBarHeight(d, group.key) }"
                             @mouseenter="showTooltip($event, rtTooltip(d, group.key))"
                             @mouseleave="hideTooltip()">
                        </div>
                    </div>
                    <div class="bar-legend">
                        <span>24 hours ago</span>
                        <span></span>
                        <span>Now</span>
                    </div>
                </div>
                </template>
            </template>

            <div v-else class="text-center py-8">
                <div class="spinner-border text-primary" role="status"></div>
            </div>

            <!-- Tooltip -->
            <div ref="tooltip" class="status-tooltip" v-show="tooltip.visible"
                 :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }">
                {{ tooltip.text }}
            </div>
        </div>
    </section>
</div>
</template>

<script>
const GROUPS = [
    { key: 'all', label: 'DataLikers (Overall)' },
    { key: 'v1', label: 'Cache API v1' },
    { key: 'v2', label: 'Cache API v2' },
]

const STATUS_LABELS = {
    operational: 'Operational',
    degraded: 'Degraded',
    partial_outage: 'Partial Outage',
    major_outage: 'Major Outage',
}

export default {
    data() {
        return {
            data: {},
            loaded: false,
            groups: GROUPS,
            tooltip: { visible: false, text: '', x: 0, y: 0 },
        }
    },
    computed: {
        bannerClass() {
            const s = this.data.current_status || 'operational'
            return 'banner-' + s
        },
        bannerTitle() {
            if (!this.data.current_status) return 'Loading...'
            if (this.data.current_status === 'operational') return 'All Systems Operational'
            return STATUS_LABELS[this.data.current_status] || this.data.current_status
        },
        maxP50() {
            if (!this.data.intervals) return 1
            let max = 0
            for (const d of this.data.intervals) {
                for (const g of GROUPS) {
                    const v = d[g.key]?.p50 || 0
                    if (v > max) max = v
                }
            }
            return max || 1
        },
    },
    created() {
        this.$services.Status().get().then(resp => {
            this.data = resp.body
            this.loaded = true
        }).catch(() => {
            this.data = { current_status: 'major_outage' }
            this.loaded = false
        })
    },
    methods: {
        barColor(d) {
            if (!d) return 'bar-gray'
            const pct = d.error_pct
            if (pct < 1) return 'bar-green'
            if (pct < 5) return 'bar-yellow'
            if (pct < 20) return 'bar-orange'
            return 'bar-red'
        },
        statusClass(status) {
            return 'label-' + status
        },
        componentStatus(key) {
            return this.data.components?.[key]?.status || 'operational'
        },
        componentStatusLabel(key) {
            return STATUS_LABELS[this.componentStatus(key)] || ''
        },
        componentUptime(key) {
            return this.data.components?.[key]?.uptime_90d ?? 100
        },
        intervalUptime(key) {
            if (!this.data.intervals?.length) return 100
            const total = this.data.intervals.length
            const ok = this.data.intervals.filter(d => (d[key]?.error_pct || 0) < 1).length
            return ((100 * ok / total)).toFixed(2)
        },
        lastInterval(key) {
            if (!this.data.intervals?.length) return null
            return this.data.intervals[this.data.intervals.length - 1][key]
        },
        rtBarHeight(d, key) {
            const p50 = d[key]?.p50 || 0
            return Math.min(100, (p50 / this.maxP50) * 100) + '%'
        },
        formatUTC(str) {
            try {
                return new Date(str).toUTCString()
            } catch { return str }
        },
        dailyTooltip(d, key) {
            const pct = d[key]?.error_pct
            return d.date + (pct != null ? ' — ' + pct + '% errors' : ' — no data')
        },
        intervalTooltip(d, key) {
            const v = d[key]
            if (!v) return 'No data'
            const t = d.time?.substring(11, 16) || ''
            return t + ' — ' + v.error_pct + '% errors'
        },
        rtTooltip(d, key) {
            const v = d[key]
            if (!v) return 'No data'
            const t = d.time?.substring(11, 16) || ''
            return t + ' — p50: ' + v.p50 + 'ms'
        },
        showTooltip(event, text) {
            const rect = event.target.getBoundingClientRect()
            this.tooltip = {
                visible: true,
                text,
                x: rect.left + rect.width / 2,
                y: rect.top - 8,
            }
        },
        hideTooltip() {
            this.tooltip.visible = false
        },
    },
}
</script>

<style scoped>
.status-banner {
    text-align: center;
    padding: 16px;
    border-radius: 8px;
    margin-bottom: 32px;
}
.banner-operational { background: #f0fdf4; border: 1px solid #bbf7d0; }
.banner-degraded { background: #fefce8; border: 1px solid #fde68a; }
.banner-partial_outage { background: #fff7ed; border: 1px solid #fed7aa; }
.banner-major_outage { background: #fef2f2; border: 1px solid #fecaca; }

.status-banner-title {
    margin: 0 0 4px;
    font-size: 20px;
    font-weight: 600;
}
.banner-operational .status-banner-title { color: #15803d; }
.banner-degraded .status-banner-title { color: #a16207; }
.banner-partial_outage .status-banner-title { color: #c2410c; }
.banner-major_outage .status-banner-title { color: #dc2626; }

.status-banner-sub {
    margin: 0;
    font-size: 14px;
    color: #6b7280;
}

.section-title {
    font-size: 13px;
    font-weight: 500;
    color: #6b7280;
    margin-bottom: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.status-section { margin-bottom: 28px; }

.status-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}
.status-name { font-weight: 600; font-size: 16px; }

.status-label {
    font-size: 14px;
    padding: 2px 10px;
    border-radius: 12px;
}
.label-operational { color: #15803d; background: #dcfce7; }
.label-degraded { color: #a16207; background: #fef9c3; }
.label-partial_outage { color: #c2410c; background: #ffedd5; }
.label-major_outage { color: #dc2626; background: #fee2e2; }

.uptime-bars {
    display: flex;
    gap: 1px;
    height: 24px;
    border-radius: 3px;
    overflow: hidden;
    margin-bottom: 4px;
}
.uptime-bars.large { height: 32px; }

.uptime-bar {
    flex: 1;
    min-width: 0;
    border-radius: 1px;
    cursor: pointer;
    transition: opacity 0.15s;
}
.uptime-bar:hover { opacity: 0.8; transform: scaleY(1.15); }

.bar-green { background: #22c55e; }
.bar-yellow { background: #eab308; }
.bar-orange { background: #f97316; }
.bar-red { background: #ef4444; }
.bar-gray { background: #e5e7eb; }

.bar-legend {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    color: #9ca3af;
}

.divider {
    border: none;
    border-top: 1px solid #e5e7eb;
    margin: 24px 0;
}

.current-stats {
    display: flex;
    gap: 24px;
    margin-bottom: 8px;
    font-size: 13px;
    color: #6b7280;
}
.current-stats span { font-weight: 600; color: #374151; }

.rt-bars {
    display: flex;
    gap: 0;
    height: 40px;
    align-items: flex-end;
    margin-bottom: 4px;
}
.rt-bar {
    flex: 1;
    min-width: 0;
    background: #818cf8;
    cursor: pointer;
    transition: opacity 0.15s;
}
.rt-bar:hover { opacity: 0.7; }

.status-tooltip {
    position: fixed;
    background: #1f2937;
    color: white;
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 12px;
    white-space: nowrap;
    transform: translate(-50%, -100%);
    pointer-events: none;
    z-index: 100;
}
</style>
